// The activity of an email sent through Gmail (pure, tested by scripts/test-sent-activity.mjs; agreed 04-11-2026).
// Gmail accepting the email and the activity existing are two different facts: «registrado» is said only when the
// activity is found in Airtable by its Gmail id ([gmsg:…] in Notes). Recovering it reuses that id and checks first,
// so an email is never logged twice; the email is never sent again.
import { opMark } from './followupPanel.js';
import { formatActivityDate, parseActivityDate, todayTimeZone, INSTANT_MARK } from './activityDate.js';

const byCreation = (a, b) => String(a.createdTime || '').localeCompare(String(b.createdTime || '')) || String(a.id).localeCompare(String(b.id));
const notesOf = (r) => String(r?.fields?.Notes ?? '');

/**
 * The activity of one Gmail message, exactly once: if one already exists (gmail-send logged it, or an earlier
 * recovery) it is reused; otherwise it is created with the Gmail id and the operation mark, read back, and only the
 * extra copies that carry this same operation are removed (never a record written by someone else).
 *   findByGmsg(id) → records; create(fields) → record; remove(id) → void
 */
export async function ensureSentActivity({ findByGmsg, create, remove, gmailId, op, fields }) {
  if (!/^[A-Za-z0-9_-]{4,64}$/.test(String(gmailId || ''))) throw new Error('id de Gmail inválido');
  const before = (await findByGmsg(gmailId)).sort(byCreation);
  if (before.length) return { record: before[0], created: false };
  await create(fields);
  const after = (await findByGmsg(gmailId)).sort(byCreation);
  for (const extra of after.slice(1)) {
    if (notesOf(extra).includes(opMark(op))) { try { await remove(extra.id); } catch { /* the other request removed it */ } }
  }
  if (!after.length) throw new Error('la actividad no se pudo comprobar');
  return { record: after[0], created: true };
}

/** The fields of a recovered activity: the same shape gmail-send writes, with the Gmail id first and the op mark. */
export function sentActivityFields({ gmailId, op, subject = '', body = '', stakeholderId, accountIds = [], name = '', sentAt = '', loggedBy = '' }) {
  return {
    'Activity Name': `Email — ${String(subject).trim() || name}`.slice(0, 200), 'Channel': 'Email', 'Status': 'Sent',
    'Notes': `[gmsg:${gmailId}] ${opMark(op)} ${INSTANT_MARK} ${String(subject).trim()}`.trim(), 'Message': String(body).trim().slice(0, 1000),
    'Stakeholder': [stakeholderId], ...(accountIds.length ? { 'Account': accountIds } : {}),
    // The instant the email was sent (Gmail's time, kept in the task), never the day it is recovered
    'Date': sentInstant(sentAt), 'Logged By': loggedBy,
  };
}

/** A valid ISO instant of the sending, or throws: a recovery never dates the email with the day it is recovered. */
export function sentInstant(sentAt) {
  const ms = new Date(String(sentAt || '')).getTime();
  if (!sentAt || Number.isNaN(ms)) throw new Error('falta el momento del envío');
  return new Date(ms).toISOString();
}

/** What the screen says about a found activity: channel, status, date, contact and company (or «sin empresa»). */
export function activitySummary(record, { stakeholders = [], accounts = [], tz = todayTimeZone() } = {}) {
  const f = record?.fields || {};
  const ids = (k) => (Array.isArray(f[k]) ? f[k].map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean) : []);
  const contact = ids('Stakeholder').map(id => stakeholders.find(s => s.id === id)).filter(Boolean).map(s => `${s.fields?.Name || ''} ${s.fields?.['Last name'] || ''}`.trim());
  const company = ids('Account').map(id => accounts.find(a => a.id === id)).filter(Boolean).map(a => a.fields?.['Account Name'] || '');
  const sel = (v) => (v && typeof v === 'object' ? v.name || '' : v || '');
  return {
    id: record?.id || '', channel: sel(f.Channel), status: sel(f.Status), date: formatActivityDate(record, tz), dateKind: parseActivityDate(f.Date)?.kind || null,
    contact: contact.join(', ') || (ids('Stakeholder').length ? 'contacto vinculado' : 'sin contacto vinculado'),
    company: company.join(', ') || (ids('Account').length ? 'empresa vinculada' : 'sin empresa'),
    linked: ids('Stakeholder').length > 0,
  };
}
